import {
  CourseStructuredData,
  EducationalProgramStructuredData,
} from "design-system/components/primitives/head/head";
import {
  LandingPageEntry,
  ProgramPageEntry,
} from "frontend/contentful/schema/pages";
import { SiteEntry } from "frontend/contentful/schema/universal";
import { useContentful } from "frontend/hooks/use-contentful";
import { getOrgStructuredData } from "./get-org-structured-data";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { TeaseRowCourseCardsEntry } from "frontend/contentful/schema/blocks";
import { CourseCardsProps } from "design-system/sites/online/components/course-card-tease/course-card-tease";
import { createTeaseRowCourseCardsProps } from "../blocks/teases/tease-row-course-cards.props";
import { documentToString } from "frontend/contentful/lib/document-to-string";

export function getProgramStructuredData(
  entry: LandingPageEntry | ProgramPageEntry,
  site: SiteEntry,
): EducationalProgramStructuredData {
  const { getEntry } = useContentful();

  const providerFull = getOrgStructuredData(site, true);
  const providerId = getOrgStructuredData(site, false);

  let description: string | undefined = undefined;
  if (
    entry.fields.description &&
    typeof entry.fields.description === "object" &&
    "nodeType" in entry.fields.description
  ) {
    description = documentToString(entry.fields.description);
  } else if (typeof entry.fields.description === "string") {
    description = entry.fields.description;
  }

  // SEO structured data specific to HBS Online currently
  const programDataProps: EducationalProgramStructuredData = {
    name: entry.fields.title,
    url: entry.fields.url,
    description: description,
    provider: providerFull,
  };

  const courseDataProps: Array<CourseStructuredData> = [];
  let courseCardProps: Array<CourseCardsProps> = [];

  if ("blocks" in entry.fields && entry.fields.blocks) {
    entry.fields.blocks.forEach((block) => {
      const entry = getEntry(block);

      if (
        isContentType<TeaseRowCourseCardsEntry>("teaseRowCourseCards", entry)
      ) {
        const teaseRowProps =
          createTeaseRowCourseCardsProps(entry)?.teases || [];

        if (teaseRowProps.length > 0) {
          courseCardProps = courseCardProps.concat(teaseRowProps);
        }
      }
    });
  }

  courseCardProps?.forEach((course) => {
    courseDataProps.push({
      "@type": "Course",
      name: course.title,
      url: course.link,
      description: course.description,
      provider: providerId,
      category:
        course.category ||
        (course.title ? [course.title] : ["HBS Online Course"]),
      offers: {
        "@type": "Offer",
        url: course.link,
        price:
          course.sessions && course.sessions[0]?.fee
            ? parseFloat(course.sessions[0].fee.replace(/[^0-9.-]+/g, ""))
            : undefined,
        priceCurrency: "USD",
        itemCondition: "https://schema.org/NewCondition",
        availability: "https://schema.org/InStock",
      },
      hasCourseInstance:
        course?.sessions?.map((session) => ({
          "@type": "CourseInstance",
          courseMode: "online",
          courseSchedule: {
            "@type": "Schedule",
            startDate: session.startDate,
            endDate: session.endDate,
          },
        })) || undefined,
    });
  });

  if (courseDataProps.length > 0) {
    programDataProps.hasCourse = courseDataProps;
  }

  return programDataProps;
}
